import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '../../context/CartContext';
import './PromoCodeModal.css';

// Popup central para aplicar código promocional. Se abre sobre el dropdown
// del carrito o sobre /cart, con backdrop oscurecido + blur. Reusa
// applyPromoCode del CartContext (ya cableado a Medusa Promotions).
export default function PromoCodeModal({ isOpen, onClose }) {
  const { applyPromoCode } = useCart();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Reset al abrir y foco en input
  useEffect(() => {
    if (!isOpen) return;
    setCode('');
    setStatus('idle');
    setMessage('');
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Limpiar timer de auto-cierre si el modal se desmonta antes
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  // ESC para cerrar
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && status !== 'loading') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, status, onClose]);

  const handleApply = async (e) => {
    e?.preventDefault();
    if (status === 'loading') return;
    const trimmed = code.trim();
    if (!trimmed) return;

    setStatus('loading');
    setMessage('');
    try {
      await applyPromoCode(trimmed);
      setStatus('success');
      setMessage('Cupón aplicado correctamente.');
      closeTimerRef.current = setTimeout(() => onClose(), 1200);
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'No se pudo aplicar el código.');
    }
  };

  if (!isOpen) return null;

  const applyDisabled = status === 'loading' || !code.trim();

  return createPortal(
    <div
      className="promo-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && status !== 'loading') onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-modal-title"
    >
      <div className="promo-modal-card">
        <button
          type="button"
          className="promo-modal-close"
          onClick={onClose}
          disabled={status === 'loading'}
          aria-label="Cerrar"
        >
          ×
        </button>

        <h2 id="promo-modal-title" className="promo-modal-title">
          ¿Tienes un código promocional?
        </h2>

        <form className="promo-modal-form" onSubmit={handleApply}>
          <div className="promo-modal-input-wrap">
            <input
              ref={inputRef}
              type="text"
              className="promo-modal-input"
              placeholder="Introduce el código"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={status === 'loading'}
              autoComplete="off"
              aria-label="Código promocional"
            />
            <button
              type="submit"
              className="promo-modal-input-plus"
              disabled={applyDisabled}
              aria-label="Aplicar código"
              tabIndex={-1}
            >
              +
            </button>
          </div>

          <button
            type="submit"
            className="promo-modal-apply"
            disabled={applyDisabled}
          >
            {status === 'loading' ? (
              <span className="promo-modal-dots" aria-label="Validando">
                <span /><span /><span />
              </span>
            ) : (
              'Aplicar'
            )}
          </button>
        </form>

        {status === 'error' && (
          <p className="promo-modal-msg promo-modal-msg--error" role="alert">
            {message}
          </p>
        )}
        {status === 'success' && (
          <p className="promo-modal-msg promo-modal-msg--ok" role="status">
            {message}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}
